import { DOCUMENT } from '@angular/common';
import { inject, Injectable, Renderer2, RendererFactory2, signal } from '@angular/core';

import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { LoggingService } from './logging.service';

/**
 * JV Partner footer configuration (legal URLs, NMLS, copyright)
 */
export interface JvFooterConfig {
  privacyUrl: string;
  licensingUrl: string;
  termsOfUseUrl: string;
  accessibilityUrl: string;
  nmlsId: string;
  consumerAccessUrl: string;
  /** Copyright text (e.g. "©2024-2025 Ridgeland Mortgage, LLC. ...") */
  copyright: string;
  companyNameLegal: string;
}

export interface JvThemeConfig {
  cssClass: string;
  cssFile: string;
  logoUrl: string;
  displayName: string;
  primaryColor: string;
  contactPhone?: string;
  backgroundImage?: string;
  secondaryColor?: string;
  actionColor?: string;
  headerBgColor?: string;
  cardBgColor?: string;
  myTasksHeaderBgColor?: string;
  completedTasksHeaderBgColor?: string;
  docRowHighlightBgColor?: string;
  pendingRowBgColor?: string;
  pendingTasksHeaderBgColor?: string;
  headerActiveColor?: string;
  loginHeaderTextColor?: string;
  footerBgColor?: string;
  useLightFooterText?: boolean;
  useLightText?: boolean;
  ehoIconUrl?: string;
  faviconUrl?: string;
  startShowingButtonColor?: string;
  startShowingButtonTextColor?: string;
  /** Footer-specific legal URLs, NMLS, and copyright */
  footer?: JvFooterConfig;
}

const RETIRED_PRODUCTION_DOMAINS: Set<string> = new Set([
  'imaginationhomes.loandepot.com',
  'farmbureau.mortgage',
  'www.farmbureau.mortgage',
  'onxxplusmortgage.com',
  'www.onxxplusmortgage.com',
]);

const RETIRED_JV_SUBDOMAIN_IDS: Set<string> = new Set(['imaginationhomes', 'fbm', 'farmbureau', 'onx']);

const PRODUCTION_DOMAIN_MAP: Record<string, string> = {
  'mscmortgage.com': 'mscm',
  'www.mscmortgage.com': 'mscm',
  'heartwoodmortgage.com': 'hmllc',
  'www.heartwoodmortgage.com': 'hmllc',
  'brphomemortgage.com': 'brphm',
  'www.brphomemortgage.com': 'brphm',
  'henlopenmortgage.com': 'hm',
  'www.henlopenmortgage.com': 'hm',
  'lgimortgagesolutions.com': 'lgims',
  'www.lgimortgagesolutions.com': 'lgims',
  'mthmortgage.com': 'mthm',
  'www.mthmortgage.com': 'mthm',
  'nhcmortgage.com': 'nhcm',
  'www.nhcmortgage.com': 'nhcm',
  'obhloans.loandepot.com': 'obhloans',
  'olivebranchhomeloans.com': 'obhloans',
  'www.olivebranchhomeloans.com': 'obhloans',
  'surefirstlending.com': 'sfl',
  'www.surefirstlending.com': 'sfl',
};

/**
 * Suffix-based domain map for production. Matches any subdomain of these base domains
 * (e.g., consumerportal.mthmortgage.com, my.mthmortgage.com).
 * Checked when an exact match in PRODUCTION_DOMAIN_MAP is not found.
 */
const PRODUCTION_DOMAIN_SUFFIX_MAP: Array<{ suffix: string; partnerId: string }> = [
  { suffix: 'mscmortgage.com', partnerId: 'mscm' },
  { suffix: 'heartwoodmortgagellc.com', partnerId: 'hmllc' },
  { suffix: 'brphomemortgage.com', partnerId: 'brphm' },
  { suffix: 'henlopenmortgage.com', partnerId: 'hm' },
  { suffix: 'lgimortgagesolutions.com', partnerId: 'lgims' },
  { suffix: 'mthmortgage.com', partnerId: 'mthm' },
  { suffix: 'nhc-mortgage.com', partnerId: 'nhcm' },
  { suffix: 'olivebranchhomeloans.com', partnerId: 'obhloans' },
  { suffix: 'ridgelandmortgage.com', partnerId: 'ridgeland' },
  { suffix: 'surefirstlending.com', partnerId: 'sfl' },
];

const SUBDOMAIN_ALIAS_MAP: Record<string, string> = {
  brp: 'brphm',
  heartwood: 'hmllc',
  henlopen: 'hm',
  msc: 'mscm',
  mth: 'mthm',
  nhc: 'nhcm',
  'brp.mla': 'brphm',
  'brookfield.mla': 'brphm',
  'msc.mla': 'mscm',
  'mscmortgage.mla': 'mscm',
  'mth.mla': 'mthm',
  'mthmortgage.mla': 'mthm',
  'henlopen.mla': 'hm',
  'hlp.mla': 'hm',
  'lgims.mla': 'lgims',
  'hwm.mla': 'hmllc',
  'nhc.mla': 'nhcm',
  'rlm.mla': 'ridgeland',
  obh: 'obhloans',
  'obh.mla': 'obhloans',
  'sfl.mla': 'sfl',
};

const VALID_JV_PARTNER_IDS = [
  'brphm',
  'hm',
  'hmllc',
  'lgims',
  'mscm',
  'mthm',
  'nhcm',
  'obhloans',
  'ridgeland',
  'brp',
  'heartwood',
  'henlopen',
  'msc',
  'mth',
  'nhc',
  'obh',
  'brp.mla',
  'brookfield.mla',
  'msc.mla',
  'mscmortgage.mla',
  'mth.mla',
  'mthmortgage.mla',
  'henlopen.mla',
  'hlp.mla',
  'lgims.mla',
  'hwm.mla',
  'nhc.mla',
  'rlm.mla',
  'obh.mla',
  'sfl',
  'sfl.mla',
];

function buildJvFooter(opts: {
  baseDomain: string;
  nmlsId: string;
  companyNameLegal: string;
  copyright: string;
  overrides?: Partial<JvFooterConfig>;
}): JvFooterConfig {
  const baseUrl = `https://www.${opts.baseDomain}`;
  return {
    privacyUrl: `${baseUrl}/privacy-statement`,
    licensingUrl: `${baseUrl}/licensing`,
    termsOfUseUrl: `${baseUrl}/terms-of-use`,
    accessibilityUrl: `${baseUrl}/ada-accessibility-statement`,
    nmlsId: opts.nmlsId,
    consumerAccessUrl: `http://www.nmlsconsumeraccess.org/EntityDetails.aspx/COMPANY/${opts.nmlsId}`,
    copyright: opts.copyright,
    companyNameLegal: opts.companyNameLegal,
    ...opts.overrides,
  };
}

const JV_THEME_MAP: Record<string, JvThemeConfig> = {
  brphm: {
    cssClass: 'jv-brphm',
    cssFile: 'themes/_jv-brphm.css',
    logoUrl: '/assets/brands/brp/img/header_logo.svg',
    displayName: 'BRP Home Mortgage',
    primaryColor: '#00467f',
    secondaryColor: '#009ec8',
    myTasksHeaderBgColor: '#00467f',
    docRowHighlightBgColor: '#e6edf3',
    contactPhone: '(877) 388-5355',
    useLightText: true,
    backgroundImage: '/assets/brands/brp/img/brp-mla.jpg',
    ehoIconUrl: '/assets/brands/brp/img/eho.png',
    faviconUrl: '/assets/brands/brp/favicon.ico',
    footer: buildJvFooter({
      baseDomain: 'brphomemortgage.com',
      nmlsId: '2044252',
      companyNameLegal: 'BRP Home Mortgage, LLC',
      copyright: '©2014-2026 BRP Home Mortgage, LLC. Equal Housing Lender. All Rights Reserved.',
    }),
  },
  hm: {
    cssClass: 'jv-hm',
    cssFile: 'themes/_jv-hm.css',
    logoUrl: '/assets/brands/henlopen/img/header_logo.svg',
    displayName: 'Henlopen Mortgage',
    primaryColor: '#00203d',
    secondaryColor: '#7ca7ad',
    myTasksHeaderBgColor: '#00203d',
    docRowHighlightBgColor: '#e6e9ec',
    contactPhone: '(412) 979-9947',
    useLightText: true,
    backgroundImage: '/assets/brands/henlopen/img/hlp-mla.jpg',
    ehoIconUrl: '/assets/brands/henlopen/img/eho.png',
    faviconUrl: '/assets/brands/henlopen/favicon.ico',
    footer: buildJvFooter({
      baseDomain: 'henlopenmortgage.com',
      nmlsId: '2051142',
      companyNameLegal: 'Henlopen Mortgage, LLC',
      copyright: '©2014-2026 Henlopen Mortgage, LLC. Equal Housing Lender. All Rights Reserved.',
    }),
  },
  hmllc: {
    cssClass: 'jv-hmllc',
    cssFile: 'themes/_jv-hmllc.css',
    logoUrl: '/assets/brands/heartwood/img/hwm_logo.svg',
    displayName: 'Heartwood Mortgage LLC',
    primaryColor: '#9a5724',
    secondaryColor: '#9a5724',
    headerBgColor: '#0c3c60',
    startShowingButtonColor: '#0c3c60',
    cardBgColor: '#0c3c60',
    myTasksHeaderBgColor: '#0c3c60',
    docRowHighlightBgColor: '#f5efea',
    contactPhone: '(877) 388-5352',
    useLightText: true,
    backgroundImage: '/assets/brands/heartwood/img/hwm-mla.jpg',
    ehoIconUrl: '/assets/brands/heartwood/img/eho.png',
    faviconUrl: '/assets/brands/heartwood/favicon.ico',
    footer: buildJvFooter({
      baseDomain: 'heartwoodmortgagellc.com',
      nmlsId: '2160975',
      companyNameLegal: 'Heartwood Mortgage, LLC',
      copyright:
        '©2026 Heartwood Mortgage, LLC, DBA Heartwood United Mortgage, LLC, Equal Housing Lender. All Rights Reserved.',
    }),
  },
  lgims: {
    cssClass: 'jv-lgims',
    cssFile: 'themes/_jv-lgims.css',
    logoUrl: '/assets/brands/lgims/img/lgi_logo.svg',
    displayName: 'LGI Mortgage Solutions',
    primaryColor: '#043345',
    secondaryColor: '#ebb425',
    actionColor: '#ebb425',
    startShowingButtonColor: '#ebb422',
    startShowingButtonTextColor: '#222222',
    headerBgColor: '#043345',
    cardBgColor: '#043345',
    myTasksHeaderBgColor: '#043345',
    docRowHighlightBgColor: '#fdf8e9',
    footerBgColor: '#ffffff',
    useLightFooterText: false,
    contactPhone: '(877) 388-8706',
    useLightText: true,
    backgroundImage: '/assets/brands/lgims/img/lgims-mla.jpg',
    ehoIconUrl: '/assets/brands/lgims/img/eho.png',
    faviconUrl: '/assets/brands/lgims/favicon.ico',
    footer: buildJvFooter({
      baseDomain: 'lgimortgagesolutions.com',
      nmlsId: '2152984',
      companyNameLegal: 'LGI Mortgage Solutions, LLC',
      copyright: '©2026 LGI Mortgage Solutions, LLC, Equal Housing Lender. All Rights Reserved.',
      overrides: { accessibilityUrl: 'https://www.lgimortgagesolutions.com/accessibility' },
    }),
  },
  mscm: {
    cssClass: 'jv-mscm',
    cssFile: 'themes/_jv-mscm.css',
    logoUrl: '/assets/brands/msc/img/header_logo.svg',
    displayName: 'MSC Mortgage',
    primaryColor: '#003155',
    secondaryColor: '#9ac5e2',
    myTasksHeaderBgColor: '#003155',
    docRowHighlightBgColor: '#e6ebee',
    contactPhone: '(480) 627-0100',
    useLightText: true,
    backgroundImage: '/assets/brands/msc/img/msc-mla.jpg',
    ehoIconUrl: '/assets/brands/msc/img/eho.png',
    faviconUrl: '/assets/brands/msc/favicon.ico',
    footer: buildJvFooter({
      baseDomain: 'mscmortgage.com',
      nmlsId: '1209513',
      companyNameLegal: 'MSC Mortgage, LLC',
      copyright: '©2014-2026 MSC Mortgage, LLC. Equal Housing Lender. All Rights Reserved.',
    }),
  },
  mthm: {
    cssClass: 'jv-mthm',
    cssFile: 'themes/_jv-mthm.css',
    logoUrl: '/assets/brands/mth/img/header_logo.svg',
    displayName: 'MTH Mortgage',
    primaryColor: '#1b5698',
    secondaryColor: '#e6eef5',
    myTasksHeaderBgColor: '#002f56',
    completedTasksHeaderBgColor: '#1b5698',
    docRowHighlightBgColor: '#e9eff5',
    contactPhone: '(877) 388-5362',
    useLightText: true,
    backgroundImage: '/assets/brands/mth/img/mth-mla.jpg',
    ehoIconUrl: '/assets/brands/mth/img/eho.png',
    faviconUrl: '/assets/brands/mth/favicon.ico',
    footer: buildJvFooter({
      baseDomain: 'mthmortgage.com',
      nmlsId: '167881',
      companyNameLegal: 'MTH Mortgage, LLC',
      copyright: '©2015-2026 MTH Mortgage, LLC. Equal Housing Lender. All Rights Reserved.',
    }),
  },
  nhcm: {
    cssClass: 'jv-nhcm',
    cssFile: 'themes/_jv-nhcm.css',
    logoUrl: '/assets/brands/nhc/img/nhc_logo.png',
    displayName: 'NHC Mortgage',
    primaryColor: '#003366',
    secondaryColor: '#369044',
    myTasksHeaderBgColor: '#003366',
    completedTasksHeaderBgColor: '#369044',
    pendingTasksHeaderBgColor: '#369044',
    docRowHighlightBgColor: '#ebf4ed',
    footerBgColor: '#fafbfc',
    useLightFooterText: false,
    contactPhone: '(855) 329-3490',
    useLightText: true,
    backgroundImage: '/assets/brands/nhc/img/nhc-mla.jpg',
    ehoIconUrl: '/assets/brands/nhc/img/eho.png',
    faviconUrl: '/assets/brands/nhc/favicon.ico',
    footer: buildJvFooter({
      baseDomain: 'nhc-mortgage.com',
      nmlsId: '2242691',
      companyNameLegal: 'NHC Mortgage, LLC',
      copyright: '©2026 NHC Mortgage, Equal Housing Lender. All Rights Reserved.',
    }),
  },

  ridgeland: {
    cssClass: 'jv-ridgeland',
    cssFile: 'themes/_jv-ridgeland.css',
    logoUrl: '/assets/brands/ridgeland/img/header_logo.svg',
    displayName: 'Ridgeland Mortgage',
    primaryColor: '#4d5a31',
    secondaryColor: '#425220',
    myTasksHeaderBgColor: '#4d5a31',
    completedTasksHeaderBgColor: '#4d5a31',
    docRowHighlightBgColor: '#eeefeb',
    contactPhone: '(833) 817-3090',
    useLightText: true,
    backgroundImage: '/assets/brands/ridgeland/img/rlm-mla.jpg',
    ehoIconUrl: '/assets/brands/ridgeland/img/eho.png',
    faviconUrl: '/assets/brands/ridgeland/favicon.ico',
    footer: buildJvFooter({
      baseDomain: 'ridgelandmortgage.com',
      nmlsId: '2245922',
      companyNameLegal: 'Ridgeland Mortgage, LLC',
      copyright: '©2024 - 2026 Ridgeland Mortgage, LLC. Equal Housing Lender. All Rights Reserved.',
    }),
  },

  sfl: {
    cssClass: 'jv-sfl',
    cssFile: 'themes/_jv-sfl.css',
    logoUrl: '/assets/brands/surefirst/img/header_logo.svg',
    displayName: 'SureFirst Lending',
    primaryColor: '#1B6333',
    secondaryColor: '#C29E62',
    myTasksHeaderBgColor: '#1B6333',
    completedTasksHeaderBgColor: '#1B6333',
    docRowHighlightBgColor: '#FAF5EC',
    contactPhone: '(800) 287-6695',
    useLightText: true,
    backgroundImage: '/assets/brands/surefirst/img/sfl-mla.jpg',
    ehoIconUrl: '/assets/brands/surefirst/img/eho.png',
    faviconUrl: '/assets/brands/surefirst/favicon.ico',
    footer: buildJvFooter({
      baseDomain: 'surefirstlending.com',
      nmlsId: '2820231',
      companyNameLegal: 'SureFirst Lending, LLC',
      copyright: '© 2026 SureFirst Lending, LLC. Equal Housing Opportunity. All Rights Reserved.',
      overrides: {
        consumerAccessUrl: 'https://www.nmlsconsumeraccess.org/TuringTestPage.aspx?ReturnUrl=/EntityDetails.aspx/COMPANY/2820231',
      },
    }),
  },

  obhloans: {
    cssClass: 'jv-obhloans',
    cssFile: 'themes/_jv-obhloans.css',
    logoUrl: '/assets/brands/obhloans/img/header_logo.png',
    displayName: 'Olive Branch Home Loans',
    primaryColor: '#4a6741',
    secondaryColor: '#8b6914',
    contactPhone: '(806) 454-9464',
    useLightText: true,
    backgroundImage: '/assets/brands/obhloans/img/obh-mla.jpg',
    ehoIconUrl: '/assets/brands/obhloans/img/eho.png',
    faviconUrl: '/assets/brands/obhloans/favicon.ico',
    footer: buildJvFooter({
      baseDomain: 'olivebranchhomeloans.com',
      nmlsId: '2500551',
      companyNameLegal: 'Olive Branch Home Loans',
      copyright: '©2026 Olive Branch Home Loans LLC. Equal Housing Lender. All Rights Reserved.',
    }),
  },
};

/**
 * Maps internal JV theme IDs to X-Partner-Id header codes.
 * JVs not in this map default to 'loandepot'.
 */
const X_PARTNER_ID_MAP: Record<string, string> = {
  brphm: 'brp',
  hm: 'hlp',
  hmllc: 'hwm',
  lgims: 'lgims',
  mscm: 'msc',
  mthm: 'mth',
  nhcm: 'nhc',
  ridgeland: 'rlm',
  obhloans: 'obh',
  sfl: 'sfl',
};

/**
 * Maps internal JV theme IDs to partner codes used exclusively in the HostInfo header.
 * Cloned from X_PARTNER_ID_MAP with overrides for heartwood (hwm) and henlopen (hlp).
 * NOTE: This is NOT used for the x-partner-id header — that still uses X_PARTNER_ID_MAP via getXPartnerId().
 */
const HOSTINFO_PARTNER_ID_MAP: Record<string, string> = {
  brphm: 'brp',
  hm: 'hlp',
  hmllc: 'hwm',
  lgims: 'lgims',
  mscm: 'msc',
  mthm: 'mth',
  nhcm: 'nhc',
  ridgeland: 'rlm',
  obhloans: 'obh',
  sfl: 'sfl',
};

/**
 * Maps internal JV theme IDs to their production CP API URLs.
 * The default/fallback (loanDepot) URL comes from the environment file.
 */
const JV_CPAPI_URL_MAP: Record<string, string> = {
  brphm: 'https://cpapi.brphomemortgage.com',
  hm: 'https://cpapi.henlopenmortgage.com',
  hmllc: 'https://cpapi.heartwoodmortgagellc.com',
  lgims: 'https://cpapi.lgimortgagesolutions.com',
  mscm: 'https://cpapi.mscmortgage.com',
  mthm: 'https://cpapi.mthmortgage.com',
  nhcm: 'https://cpapi.nhc-mortgage.com',
  obhloans: 'https://cpapi.olivebranchhomeloans.com',
  ridgeland: 'https://cpapi.ridgelandmortgage.com',
  sfl: 'https://cpapi.surefirstlending.com',
};

const DEFAULT_LOGO_URL = '/assets/images/loandepot-logo-3d-dark-metallic.svg';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly env = inject(ENVIRONMENT);
  private readonly rendererFactory = inject(RendererFactory2);
  private readonly loggingService = inject(LoggingService);
  private renderer: Renderer2;

  private readonly currentThemeSignal = signal<string>('default');

  private readonly isJvThemeActiveSignal = signal<boolean>(false);

  private currentThemeConfig: JvThemeConfig | null = null;

  private themeLinkElement: HTMLLinkElement | null = null;

  private isDomainDerivedJv = false;

  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  initialize(): void {
    if (this.shouldRedirectRetiredJv()) {
      return;
    }

    const { partnerId: jvPartner, isDomainDerived } = this.detectJvPartner();
    this.loggingService.logInfo(
      `[ThemeService] initialize | hostname=${this.document.defaultView?.location?.hostname} | detectedPartner=${jvPartner} | isDomainDerived=${isDomainDerived}`,
    );

    if (jvPartner) {
      this.loadTheme(jvPartner);
    }

    if (isDomainDerived) {
      this.isDomainDerivedJv = true;
    }

    if (jvPartner && isDomainDerived && this.env.production) {
      const jvCpApiUrl = JV_CPAPI_URL_MAP[jvPartner];
      if (jvCpApiUrl) {
        this.env.appSettings.cpApiUrl = jvCpApiUrl;
        this.loggingService.logInfo(
          `[ThemeService] Overriding cpApiUrl for JV partner ${jvPartner}: ${jvCpApiUrl}`,
        );
      }
    }
  }

  private shouldRedirectRetiredJv(): boolean {
    const hostname = this.document.defaultView?.location?.hostname?.toLowerCase() ?? '';
    if (!hostname) return false;

    if (RETIRED_PRODUCTION_DOMAINS.has(hostname)) {
      this.redirectToBaseLoanDepot();
      return true;
    }

    const isLowerEnvironment = hostname.includes('-dv1.') || hostname.includes('-qa1.') || hostname.includes('-sg1.');
    if (isLowerEnvironment) {
      const subdomain = hostname.split('.')[0].toLowerCase();
      if (RETIRED_JV_SUBDOMAIN_IDS.has(subdomain)) {
        this.redirectToBaseLoanDepot();
        return true;
      }
    }

    if (hostname.endsWith('.localhost')) {
      const subdomain = hostname.split('.')[0].toLowerCase();
      if (RETIRED_JV_SUBDOMAIN_IDS.has(subdomain)) {
        this.redirectToBaseLoanDepot();
        return true;
      }
    }

    return false;
  }

  private redirectToBaseLoanDepot(): void {
    const hostname = this.document.defaultView?.location?.hostname?.toLowerCase() ?? '';
    const window = this.document.defaultView;
    if (!window) return;

    let baseUrl: string;

    const lowerEnvMatch = hostname.match(/\w+\.(consumerportal-(?:dv1|qa1|sg1)\.loandepotdev\.works)/);
    if (lowerEnvMatch) {
      baseUrl = `https://${lowerEnvMatch[1]}`;
    } else if (hostname.endsWith('.localhost') || hostname === 'localhost') {
      baseUrl = `http://localhost:${window.location.port || '4200'}`;
    } else {
      baseUrl = 'https://consumerportal.loandepot.com';
    }

    this.loggingService.logInfo(`[ThemeService] Redirecting retired JV domain ${hostname} to ${baseUrl}`);
    window.location.href = baseUrl + (window.location.pathname || '/') + (window.location.search || '');
  }

  private detectJvPartner(): { partnerId: string | null; isDomainDerived: boolean } {
    const hostname = this.document.defaultView?.location?.hostname ?? '';
    const searchParams = new URLSearchParams(this.document.defaultView?.location?.search ?? '');

    this.loggingService.logInfo(`[ThemeService] detectJvPartner | hostname=${hostname}`);

    const jvQueryParam = searchParams.get('jv')?.toLowerCase();
    if (jvQueryParam && VALID_JV_PARTNER_IDS.includes(jvQueryParam)) {
      const resolved = this.resolvePartnerId(jvQueryParam);
      this.loggingService.logInfo(
        `[ThemeService] detectJvPartner matched via query param: jv=${jvQueryParam} -> resolved=${resolved}`,
      );
      return { partnerId: resolved, isDomainDerived: false };
    }

    if (!hostname) {
      this.loggingService.logInfo('[ThemeService] detectJvPartner: no hostname, returning null');
      return { partnerId: null, isDomainDerived: false };
    }

    const productionPartner = PRODUCTION_DOMAIN_MAP[hostname.toLowerCase()];
    if (productionPartner) {
      this.loggingService.logInfo(
        `[ThemeService] detectJvPartner matched via production domain: ${hostname} -> ${productionPartner}`,
      );
      return { partnerId: productionPartner, isDomainDerived: true };
    }

    const suffixMatch = PRODUCTION_DOMAIN_SUFFIX_MAP.find((entry) =>
      hostname.toLowerCase().endsWith(`.${entry.suffix}`),
    );
    if (suffixMatch) {
      this.loggingService.logInfo(
        `[ThemeService] detectJvPartner matched via domain suffix: ${hostname} -> ${suffixMatch.partnerId}`,
      );
      return { partnerId: suffixMatch.partnerId, isDomainDerived: true };
    }

    const isLowerEnvironment = hostname.includes('-dv1.') || hostname.includes('-qa1.') || hostname.includes('-sg1.');
    this.loggingService.logInfo(`[ThemeService] detectJvPartner | isLowerEnvironment=${isLowerEnvironment}`);

    if (isLowerEnvironment) {
      const subdomain = hostname.split('.')[0].toLowerCase();
      this.loggingService.logInfo(
        `[ThemeService] detectJvPartner lower env subdomain=${subdomain} | isValidJvId=${VALID_JV_PARTNER_IDS.includes(subdomain)}`,
      );
      if (VALID_JV_PARTNER_IDS.includes(subdomain)) {
        const resolved = this.resolvePartnerId(subdomain);
        this.loggingService.logInfo(
          `[ThemeService] detectJvPartner matched via lower env subdomain: ${subdomain} -> resolved=${resolved}`,
        );
        return { partnerId: resolved, isDomainDerived: true };
      }
    }

    if (hostname === 'localhost' || hostname.endsWith('.localhost')) {
      const subdomain = hostname.split('.')[0].toLowerCase();
      if (VALID_JV_PARTNER_IDS.includes(subdomain)) {
        const resolved = this.resolvePartnerId(subdomain);
        return { partnerId: resolved, isDomainDerived: true };
      }
    }

    this.loggingService.logInfo(`[ThemeService] detectJvPartner: no JV partner detected for hostname=${hostname}`);
    return { partnerId: null, isDomainDerived: false };
  }

  private resolvePartnerId(id: string): string {
    if (JV_THEME_MAP[id]) {
      return id;
    }
    return SUBDOMAIN_ALIAS_MAP[id] ?? id;
  }

  async loadTheme(partnerId: string): Promise<void> {
    const normalizedId = partnerId.toLowerCase();
    const themeConfig = JV_THEME_MAP[normalizedId];

    if (!themeConfig) {
      this.loggingService.logWarning(`[ThemeService] Unknown JV partner: ${partnerId}. Using default theme.`);
      return;
    }

    this.removeCurrentTheme();

    this.currentThemeConfig = themeConfig;
    this.currentThemeSignal.set(normalizedId);
    this.isJvThemeActiveSignal.set(true);

    this.renderer.addClass(this.document.body, themeConfig.cssClass);
    this.renderer.addClass(this.document.body, 'jv-theme');

    this.applyJvCssProperties(themeConfig);

    if (themeConfig.faviconUrl) {
      this.updateFavicon(themeConfig.faviconUrl);
    }

    this.document.title = themeConfig.displayName;

    try {
      await this.injectThemeCss(themeConfig.cssFile);
    } catch {
      this.loggingService.logWarning(`[ThemeService] CSS injection failed for ${themeConfig.cssFile}, continuing with inline properties.`);
    }
  }

  getCurrentTheme(): string {
    return this.currentThemeSignal();
  }

  isJvThemeActive(): boolean {
    return this.isJvThemeActiveSignal();
  }

  isNonLoanDepotJv(): boolean {
    return this.isJvThemeActiveSignal() && this.getXPartnerId() !== 'loanDepot';
  }

  getLogoUrl(): string {
    if (this.currentThemeConfig) {
      return this.currentThemeConfig.logoUrl;
    }
    return DEFAULT_LOGO_URL;
  }

  getPartnerDisplayName(): string {
    if (this.currentThemeConfig) {
      return this.currentThemeConfig.displayName;
    }
    return 'loanDepot';
  }

  getContactPhone(): string | undefined {
    return this.currentThemeConfig?.contactPhone;
  }

  getPrimaryColor(): string | undefined {
    return this.currentThemeConfig?.primaryColor;
  }

  getSecondaryColor(): string | undefined {
    return this.currentThemeConfig?.secondaryColor;
  }

  useLightText(): boolean {
    return this.currentThemeConfig?.useLightText ?? true;
  }

  useLightFooterText(): boolean {
    return this.currentThemeConfig?.useLightFooterText ?? this.useLightText();
  }

  getEhoIconUrl(): string {
    return this.currentThemeConfig?.ehoIconUrl ?? '/assets/brands/acme/img/eho.png';
  }

  getFooterConfig(): JvFooterConfig | null {
    return this.currentThemeConfig?.footer ?? null;
  }

  getBackgroundImage(): string | undefined {
    return this.currentThemeConfig?.backgroundImage;
  }

  getStartShowingButtonColor(): string {
    return this.currentThemeConfig?.startShowingButtonColor ?? this.currentThemeConfig?.primaryColor ?? '#222222';
  }

  getStartShowingButtonTextColor(): string {
    return this.currentThemeConfig?.startShowingButtonTextColor ?? '#ffffff';
  }

  getNmlsId(): string {
    return this.currentThemeConfig?.footer?.nmlsId ?? '174457';
  }

  getThemeConfig(): JvThemeConfig | null {
    return this.currentThemeConfig;
  }

  getCpApiUrl(): string {
    const theme = this.currentThemeSignal();
    if (this.env.production && this.isDomainDerivedJv && theme !== 'default' && JV_CPAPI_URL_MAP[theme]) {
      return JV_CPAPI_URL_MAP[theme];
    }
    return this.env.appSettings.cpApiUrl || '';
  }

  getXPartnerId(): string {
    const theme = this.currentThemeSignal();
    if (theme === 'default') {
      return 'loanDepot';
    }
    return X_PARTNER_ID_MAP[theme] ?? 'loanDepot';
  }

  /**
   * Returns the partner ID for use in the HostInfo header only.
   * Uses HOSTINFO_PARTNER_ID_MAP which differs from X_PARTNER_ID_MAP for heartwood and henlopen.
   * Do NOT use this for the x-partner-id header — use getXPartnerId() for that.
   */
  getHostInfoPartnerId(): string {
    const theme = this.currentThemeSignal();
    if (theme === 'default') {
      return 'loanDepot';
    }
    return HOSTINFO_PARTNER_ID_MAP[theme] ?? 'loanDepot';
  }

  private removeCurrentTheme(): void {
    if (this.currentThemeConfig) {
      this.renderer.removeClass(this.document.body, this.currentThemeConfig.cssClass);
      this.renderer.removeClass(this.document.body, 'jv-theme');
    }

    if (this.themeLinkElement) {
      this.renderer.removeChild(this.document.head, this.themeLinkElement);
      this.themeLinkElement = null;
    }

    this.clearJvCssProperties();

    this.currentThemeConfig = null;
    this.currentThemeSignal.set('default');
    this.isJvThemeActiveSignal.set(false);
  }

  private applyJvCssProperties(config: JvThemeConfig): void {
    const root = this.document.documentElement;
    const actionPrimary = config.actionColor ?? config.primaryColor;

    root.style.setProperty('--color-ld-primary', actionPrimary);

    root.style.setProperty('--color-ld-primary-50', this.lightenColor(actionPrimary, 0.92));
    root.style.setProperty('--color-ld-primary-100', this.lightenColor(actionPrimary, 0.85));
    root.style.setProperty('--color-ld-primary-500', this.lightenColor(actionPrimary, 0.3));
    root.style.setProperty('--color-ld-primary-700', this.darkenColor(actionPrimary, 0.2));
    root.style.setProperty('--color-ld-primary-900', this.darkenColor(actionPrimary, 0.4));

    const headerBg = config.headerBgColor ?? config.primaryColor;
    root.style.setProperty('--ld-header-bg', headerBg);

    root.style.setProperty('--ld-card-bg', config.cardBgColor ?? headerBg);

    root.style.setProperty('--ld-mytasks-header-bg', config.myTasksHeaderBgColor ?? config.primaryColor);

    root.style.setProperty('--ld-completed-header-bg', config.completedTasksHeaderBgColor ?? config.primaryColor);

    const docRowHighlight = config.docRowHighlightBgColor ?? this.lightenColor(config.primaryColor, 0.92);
    root.style.setProperty('--ld-doc-row-highlight-bg', docRowHighlight);

    root.style.setProperty('--ld-pending-row-bg', config.pendingRowBgColor ?? docRowHighlight);

    root.style.setProperty(
      '--ld-pending-tasks-header-bg',
      config.pendingTasksHeaderBgColor ?? config.myTasksHeaderBgColor ?? config.primaryColor,
    );

    root.style.setProperty('--ld-header-active-color', config.headerActiveColor ?? 'white');

    root.style.setProperty('--ld-login-header-text', config.loginHeaderTextColor ?? headerBg);

    root.style.setProperty('--ld-footer-bg', config.footerBgColor ?? headerBg);

    if (config.secondaryColor) {
      const secondary = config.secondaryColor;
      root.style.setProperty('--color-ld-lime', secondary);
      root.style.setProperty('--color-ld-lime-hover', this.darkenColor(secondary, 0.1));
      root.style.setProperty('--color-ld-lime-active', this.darkenColor(secondary, 0.2));
      root.style.setProperty('--color-ld-lime-dark', this.darkenColor(secondary, 0.3));
    }
  }

  private clearJvCssProperties(): void {
    const root = this.document.documentElement;
    root.style.removeProperty('--color-ld-primary');
    root.style.removeProperty('--color-ld-primary-50');
    root.style.removeProperty('--color-ld-primary-100');
    root.style.removeProperty('--color-ld-primary-500');
    root.style.removeProperty('--color-ld-primary-700');
    root.style.removeProperty('--color-ld-primary-900');
    root.style.removeProperty('--ld-header-bg');
    root.style.removeProperty('--ld-card-bg');
    root.style.removeProperty('--ld-mytasks-header-bg');
    root.style.removeProperty('--ld-completed-header-bg');
    root.style.removeProperty('--ld-doc-row-highlight-bg');
    root.style.removeProperty('--ld-pending-row-bg');
    root.style.removeProperty('--ld-pending-tasks-header-bg');
    root.style.removeProperty('--ld-header-active-color');
    root.style.removeProperty('--ld-login-header-text');
    root.style.removeProperty('--ld-footer-bg');
    root.style.removeProperty('--color-ld-lime');
    root.style.removeProperty('--color-ld-lime-hover');
    root.style.removeProperty('--color-ld-lime-active');
    root.style.removeProperty('--color-ld-lime-dark');
  }

  private lightenColor(hex: string, amount: number): string {
    return this.mixColors(hex, '#ffffff', amount);
  }

  private darkenColor(hex: string, amount: number): string {
    return this.mixColors(hex, '#000000', amount);
  }

  private mixColors(c1: string, c2: string, weight: number): string {
    const r1 = parseInt(c1.slice(1, 3), 16);
    const g1 = parseInt(c1.slice(3, 5), 16);
    const b1 = parseInt(c1.slice(5, 7), 16);
    const r2 = parseInt(c2.slice(1, 3), 16);
    const g2 = parseInt(c2.slice(3, 5), 16);
    const b2 = parseInt(c2.slice(5, 7), 16);

    const r = Math.round(r1 + (r2 - r1) * weight);
    const g = Math.round(g1 + (g2 - g1) * weight);
    const b = Math.round(b1 + (b2 - b1) * weight);

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  private injectThemeCss(cssPath: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const linkElement = this.renderer.createElement('link');
      this.renderer.setAttribute(linkElement, 'rel', 'stylesheet');
      this.renderer.setAttribute(linkElement, 'type', 'text/css');
      this.renderer.setAttribute(linkElement, 'href', `/assets/styles/${cssPath}`);
      this.renderer.setAttribute(linkElement, 'id', 'jv-theme-stylesheet');

      linkElement.onload = () => {
        resolve();
      };

      linkElement.onerror = () => {
        this.loggingService.logWarning(`[ThemeService] Failed to load theme CSS: ${cssPath}`);
        reject(new Error(`Failed to load theme CSS: ${cssPath}`));
      };

      this.renderer.appendChild(this.document.head, linkElement);
      this.themeLinkElement = linkElement;
    });
  }

  private updateFavicon(faviconUrl: string): void {
    const existingLink = this.document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (existingLink) {
      this.renderer.setAttribute(existingLink, 'href', faviconUrl);
    } else {
      const linkElement = this.renderer.createElement('link');
      this.renderer.setAttribute(linkElement, 'rel', 'icon');
      this.renderer.setAttribute(linkElement, 'type', 'image/x-icon');
      this.renderer.setAttribute(linkElement, 'href', faviconUrl);
      this.renderer.appendChild(this.document.head, linkElement);
    }
  }
}
